import { useGetPreviewTaskReviewRoles } from '@exam-room/graphql/preview';
import { useTranslation } from '@exam-room/translations';
import { useGetMyProfileQuery } from '@shared/graphql';

import { CardSkeleton, PaperSection } from '../../../../components';
import { ReviewResultProvider, useNewExam, useReviewResult } from '../../../../providers';
import { EmptyReviewState } from '../../components';
import { getReviewResultRoles, getReviewResultVariant, ReviewResultVariant } from '../../utils';
import { CommentSection } from '../comment-section';
import { VerdictForm, VerdictLabel } from '../verdict-form';

function ReviewResultSkeleton() {
  return (
    <div className='flex w-full flex-col gap-4 p-6'>
      <CardSkeleton className='h-13 w-full' />
      <CardSkeleton className='h-13 w-full' />
    </div>
  );
}

interface ReviewResultContentProps {
  variant: ReviewResultVariant.EDITABLE | ReviewResultVariant.READONLY;
  questionId: string;
  taskId: string;
}

function ReviewResultContent(props: Readonly<ReviewResultContentProps>) {
  const { variant, questionId, taskId } = props;

  const { t } = useTranslation();
  const { verdict } = useReviewResult();

  if (verdict.loading && verdict.data == null) {
    return <ReviewResultSkeleton />;
  }

  if (variant === ReviewResultVariant.EDITABLE) {
    return (
      <div className='flex w-full flex-col items-start gap-6 p-6'>
        <VerdictForm />
        <CommentSection questionId={questionId} taskId={taskId} />
      </div>
    );
  }

  if (verdict.data == null) {
    return (
      <div className='w-full p-6'>
        <EmptyReviewState message={t('examPreview.reviewResultSection.emptyReviewResult')} />
      </div>
    );
  }

  return (
    <div className='flex w-full flex-col items-start gap-6 p-6'>
      <div className='flex w-full flex-col items-start gap-2'>
        <div className='text-text-primary block text-sm font-semibold'>
          {t('examPreview.reviewResultSection.reviewResult')}{' '}
          <span className='text-red-500'>*</span>
        </div>
        <VerdictLabel verdict={verdict.data} />
      </div>
      <CommentSection isReadonly questionId={questionId} taskId={taskId} />
    </div>
  );
}

interface ReviewResultSectionProps {
  q: string;
  taskId: string;
}

export default function ReviewResultSection(props: ReviewResultSectionProps) {
  const { q, taskId } = props;

  const { questionMap } = useNewExam();

  const currentQuestion = questionMap[q] ?? {};

  const { id } = currentQuestion;

  const { t } = useTranslation();

  const { data: myProfile, loading: isMyProfileLoading } = useGetMyProfileQuery();

  const { data: taskReviewRoles, loading: isTaskReviewRolesLoading } = useGetPreviewTaskReviewRoles(
    {
      variables: { getTaskByIdInput: { taskId } },
      skip: !taskId,
    },
  );

  if (!id) return null;

  const title = t('examPreview.reviewResultSection.header');

  if (isMyProfileLoading || isTaskReviewRolesLoading) {
    return (
      <PaperSection data-renderer title={title}>
        <ReviewResultSkeleton />
      </PaperSection>
    );
  }

  const task = taskReviewRoles?.wflGetTaskById.task;

  const roles = getReviewResultRoles(myProfile?.getMyProfile.id ?? '', task);
  const variant = getReviewResultVariant(roles, task?.currentWorkflowStep.code);

  if (variant === null) return null;

  if (variant === ReviewResultVariant.HIDDEN) {
    return (
      <PaperSection data-renderer title={title}>
        <div className='w-full p-6'>
          <EmptyReviewState message={t('examPreview.reviewResultSection.emptyReviewResult')} />
        </div>
      </PaperSection>
    );
  }

  return (
    <PaperSection data-renderer title={title}>
      <ReviewResultProvider questionId={id} taskId={taskId}>
        <ReviewResultContent variant={variant} questionId={id} taskId={taskId} />
      </ReviewResultProvider>
    </PaperSection>
  );
}
